"use client";

import { Icons } from "@/components/icons";
import { siteConfig } from "@/lib/config";
import { ChevronRight } from "lucide-react";
import Link from "next/link";
import { useTranslations } from "@/lib/hooks/use-translations";
import { LocaleSwitch } from "../ui/locale-switch";

interface FooterLinkInterface {
  href: string;
  text: string;
  icon: JSX.Element;
  isTargetBlank?: boolean;
}

export default function Footer() {
  const { locale } = useTranslations();
  return (
    <footer>
      <div className="max-w-6xl mx-auto py-16 px-6 pb-0">
        <Link
          href={siteConfig.getLocalizedUrl("/", locale)}
          title={siteConfig.name}
          className="relative mr-6 flex items-center space-x-2"
        >
          <Icons.logo className="w-auto h-[40px]" />
        </Link>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 sm:grid-cols-2 mt-8">
          {siteConfig.footer(locale).map((section, index) => (
            <div key={index} className="mb-5">
              <h2 className="font-semibold">{section.title}</h2>
              <ul>
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex} className="my-2">
                    <Link
                      href={link.href}
                      className="group inline-flex cursor-pointer items-center justify-start gap-1 text-muted-foreground duration-200 hover:text-primary hover:opacity-90"
                      target={link.href.startsWith('http://') || link.href.startsWith('https://') ? "_blank" : undefined}
                    >
                      {link.icon && link.icon}
                      {link.text}
                      <ChevronRight className="h-4 w-4 translate-x-0 transform opacity-0 transition-all duration-300 ease-out group-hover:translate-x-1 group-hover:opacity-100" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="flex flex-row border-t">
          <div className="max-w-6xl mx-auto border-t py-[1.2rem] grid md:grid-cols-2 h-full justify-between w-full grid-cols-1 gap-1">
            <span className="text-sm tracking-tight text-foreground">
              Copyright © {new Date().getFullYear()}{" "}
              <Link target="_blank" href="https://savagescorp.com/" className="cursor-pointer">
                {siteConfig.name}
              </Link>{" "}
            </span>
            <ul className="flex justify-start md:justify-end text-sm tracking-tight text-foreground">
              <li className="mr-3 md:mx-4">
                <Link href={siteConfig.getLocalizedUrl("/privacy", locale)}>
                  {locale === 'de' ? 'Datenschutz' : 'Privacy Policy'}
                </Link>
              </li>
              <li className="mr-3 md:mx-4">
                <Link href={siteConfig.getLocalizedUrl("/legal", locale)}>
                  {locale === 'de' ? 'Impressum' : 'Legal'}
                </Link>
              </li>
            </ul>
          </div>
          <div className="lg:hidden py-[1.2rem]">
            <LocaleSwitch />
          </div>
        </div>
      </div>
    </footer>
  );
}
